/**
 * Backlog UI shows only top-level Epics and Stories (never Tasks).
 * Stories under an Epic that is also in backlog are hidden — the Epic represents them.
 */

export function indexIssuesById(issues) {
  const byId = new Map();
  (issues || []).forEach((row) => {
    byId.set(String(row.issueid), row);
  });
  return byId;
}

/** True when this Story should appear as its own backlog row (not under a backlog Epic). */
export function isOrphanBacklogStory(story, byId) {
  const pid = story?.parent_id;
  if (pid == null || pid === '') return true;
  const parent = byId.get(String(pid));
  if (!parent || parent.issuetype !== 'Epic') return true;
  return parent.sprint_id != null;
}

/** Epics + orphan Stories only — excludes Task/Bug and children of backlog Epics. */
export function topLevelBacklogItems(allIssues) {
  const byId = indexIssuesById(allIssues);
  return (allIssues || []).filter((item) => {
    const type = String(item.issuetype || '');
    if (type === 'Task' || type === 'Bug') return false;
    if (type === 'Epic') return true;
    if (type === 'Story') return isOrphanBacklogStory(item, byId);
    return false;
  });
}

export function countDescendants(allIssues, rootId, types = null) {
  const want = types ? new Set(types) : null;
  let stories = 0;
  let tasks = 0;
  (allIssues || []).forEach((row) => {
    if (String(row.parent_id) !== String(rootId)) return;
    const t = String(row.issuetype || '');
    if (want && !want.has(t)) return;
    if (t === 'Story') stories += 1;
    if (t === 'Task') tasks += 1;
  });
  return { stories, tasks };
}

/** For an Epic in backlog, aggregate direct child stories and their tasks. */
export function epicBacklogSummary(epic, allIssues) {
  const directStories = (allIssues || []).filter(
    (r) => String(r.parent_id) === String(epic.issueid) && r.issuetype === 'Story',
  );
  let tasks = 0;
  directStories.forEach((s) => {
    tasks += (allIssues || []).filter(
      (r) => String(r.parent_id) === String(s.issueid) && r.issuetype === 'Task',
    ).length;
  });
  return { stories: directStories.length, tasks };
}

export function storyBacklogSummary(story, allIssues) {
  const tasks = (allIssues || []).filter(
    (r) => String(r.parent_id) === String(story.issueid) && r.issuetype === 'Task',
  ).length;
  return { tasks };
}

export function partitionBacklogDisplay(allIssues) {
  const top = topLevelBacklogItems(allIssues);
  const epics = top.filter((i) => i.issuetype === 'Epic');
  const stories = top.filter((i) => i.issuetype === 'Story');
  return { epics, stories, topLevel: top };
}
